import { MAX_COLUMNS, MIN_COLUMNS, MOTIVATION_LEVELS, compareClocks, type Clock, type EnergyNow } from '@hmawiw/shared';
import { create } from 'zustand';
import { getDb } from '../db/handle';
import { readSnapshot, writeSetting, type Db, type SettingEntry } from '../db/idb';
import { clockState, tick } from './clock';

export type Locale = 'en' | 'fr';
export type Theme = 'system' | 'light' | 'dark';
export type BandPreset = 'importance' | 'horizon';
export type TimeFormat = 'auto' | '12h' | '24h';
export type Accent = 'teal' | 'blue' | 'indigo' | 'violet' | 'plum' | 'rose' | 'slate';

/** Hues kept away from the red-to-green motivation scale; each has a tuned value per theme in theme.css. */
export const ACCENTS: Accent[] = ['teal', 'blue', 'indigo', 'violet', 'plum', 'rose', 'slate'];

const THEME_KEY = 'hmawiw-theme';
/** Phone only: keep today's cards as notifications. A device choice, never synced. */
const TODAY_KEY = 'hmawiw-today';

export interface SyncedSettings {
  /** How many motivation columns, least wanted on the left; between MIN_COLUMNS and MAX_COLUMNS. */
  columnCount: number;
  columnLabels: (string | null)[];
  bandLabels: (string | null)[];
  bandPreset: BandPreset;
  locale: Locale | null;
  checkinTime: string | null;
  energyNow: EnergyNow | null;
  accent: Accent;
  timeFormat: TimeFormat;
  /** The end of the ten minutes rings like an alarm rather than notifying. */
  timerRing: boolean;
  /** The one dreaded thing picked for the day at the check-in; `day` is a local start of day. */
  pinnedToday: { cardId: string; day: number } | null;
  /** Board ids in the user's order; the phone's bar steps through them. */
  boardOrder: string[];
}

export type SettingKey = keyof SyncedSettings;

export const SETTING_KEYS: SettingKey[] = ['columnCount', 'columnLabels', 'bandLabels', 'bandPreset', 'locale', 'checkinTime', 'energyNow', 'accent', 'timeFormat', 'timerRing', 'pinnedToday', 'boardOrder'];

const DEFAULTS: SyncedSettings = {
  columnCount: MOTIVATION_LEVELS,
  columnLabels: [null, null, null],
  bandLabels: [null, null, null],
  bandPreset: 'importance',
  locale: null,
  checkinTime: null,
  energyNow: null,
  accent: 'teal',
  timeFormat: 'auto',
  timerRing: true,
  pinnedToday: null,
  boardOrder: [],
};

export interface SettingsState extends SyncedSettings {
  hydrated: boolean;
  theme: Theme;
  todayShade: boolean;
  entries: Record<string, SettingEntry>;
  hydrate(db: Db): Promise<void>;
  set<K extends SettingKey>(key: K, value: SyncedSettings[K]): Promise<void>;
  setTheme(theme: Theme): void;
  setTodayShade(on: boolean): void;
  dirtyEntries(): SettingEntry[];
  /** Server value for a key; a newer local write that is still waiting keeps precedence unless forced. */
  applyRemote(key: string, json: string, clock: Clock, force: boolean): Promise<void>;
  markSynced(key: string, sentClock: Clock): Promise<void>;
  requeueAll(): Promise<void>;
  reset(): void;
}

function readTodayShade(): boolean {
  try {
    return localStorage.getItem(TODAY_KEY) === '1';
  } catch {
    return false;
  }
}

function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  if (theme === 'system') delete document.documentElement.dataset['theme'];
  else document.documentElement.dataset['theme'] = theme;
  // The installed app's title bar and the phone browser's toolbar take this colour.
  const meta = document.querySelector('meta[name="theme-color"]');
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (meta && bg) meta.setAttribute('content', bg);
}

export function applyAccent(accent: Accent): void {
  if (typeof document === 'undefined') return;
  if (accent === 'teal') delete document.documentElement.dataset['accent'];
  else document.documentElement.dataset['accent'] = accent;
}

function parse(key: string, json: string | undefined): unknown {
  const fallback = (DEFAULTS as unknown as Record<string, unknown>)[key];
  if (json === undefined) return fallback;
  try {
    const value = JSON.parse(json) as unknown;
    // A palette name this build does not know (an older app, a newer server) falls back to the default.
    if (key === 'accent') return (ACCENTS as string[]).includes(value as string) ? value : fallback;
    if (key === 'timeFormat') return value === '12h' || value === '24h' || value === 'auto' ? value : fallback;
    if (key === 'timerRing') return typeof value === 'boolean' ? value : fallback;
    if (key === 'columnLabels') return Array.isArray(value) ? value : fallback;
    if (key === 'boardOrder') return Array.isArray(value) && value.every((v) => typeof v === 'string') ? value : fallback;
    if (key === 'columnCount') return Number.isInteger(value) && (value as number) >= MIN_COLUMNS && (value as number) <= MAX_COLUMNS ? value : fallback;
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function isSettingKey(key: string): key is SettingKey {
  return (SETTING_KEYS as string[]).includes(key);
}

export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

export const useSettings = create<SettingsState>((set, get) => ({
  ...DEFAULTS,
  hydrated: false,
  theme: 'system',
  todayShade: readTodayShade(),
  entries: {},

  async hydrate(db) {
    const snap = await readSnapshot(db);
    const entries: Record<string, SettingEntry> = {};
    for (const e of snap.settings) entries[e.key] = e;
    const theme = readTheme();
    applyTheme(theme);
    if (typeof window !== 'undefined') window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => applyTheme(get().theme));
    const values: Record<string, unknown> = {};
    for (const key of SETTING_KEYS) values[key] = parse(key, entries[key]?.json);
    applyAccent(values['accent'] as Accent);
    set({ hydrated: true, theme, entries, ...(values as unknown as SyncedSettings) });
  },

  async set(key, value) {
    const previous = get().entries[key];
    const clock = tick();
    const entry: SettingEntry = {
      key,
      json: JSON.stringify(value),
      clock,
      base: previous ? (previous.dirty ? previous.base : previous.clock) : null,
      dirty: true,
    };
    await writeSetting(getDb(), entry, clockState());
    if (key === 'accent') applyAccent(value as Accent);
    set((s) => ({ ...s, [key]: value, entries: { ...s.entries, [key]: entry } }));
  },

  setTodayShade(on) {
    try {
      localStorage.setItem(TODAY_KEY, on ? '1' : '0');
    } catch {
      // no storage
    }
    set({ todayShade: on });
  },

  setTheme(theme) {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // storage unavailable; the choice lasts for this session
    }
    applyTheme(theme);
    set({ theme });
  },

  dirtyEntries() {
    return Object.values(get().entries).filter((e) => e.dirty);
  },

  async applyRemote(key, json, clock, force) {
    const existing = get().entries[key];
    if (existing && !force) {
      if (compareClocks(existing.clock, clock) >= 0) return;
    }
    const entry: SettingEntry = { key, json, clock, base: clock, dirty: false };
    await writeSetting(getDb(), entry, null);
    if (key === 'accent') applyAccent(parse(key, json) as Accent);
    set((s) => ({ ...s, ...(isSettingKey(key) ? { [key]: parse(key, json) } : {}), entries: { ...s.entries, [key]: entry } }));
  },

  async markSynced(key, sentClock) {
    const entry = get().entries[key];
    if (!entry) return;
    const next: SettingEntry = entry.clock === sentClock ? { ...entry, base: entry.clock, dirty: false } : { ...entry, base: sentClock };
    await writeSetting(getDb(), next, null);
    set((s) => ({ entries: { ...s.entries, [key]: next } }));
  },

  async requeueAll() {
    const db = getDb();
    const entries: Record<string, SettingEntry> = {};
    for (const e of Object.values(get().entries)) {
      const next = { ...e, base: null, dirty: true };
      await writeSetting(db, next, null);
      entries[e.key] = next;
    }
    set({ entries });
  },

  reset() {
    applyAccent(DEFAULTS.accent);
    set({ ...DEFAULTS, entries: {} });
  },
}));
